import { useRef, useState } from 'react';
import { Info as InfoIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// Keeps the tooltip this far inside the page's edge.
const EDGE_PADDING = 8;
// Narrower than this to the right of the icon (only on a very small page) and the tooltip opens below instead.
const MIN_RIGHT_ROOM = 220;

interface MetricInfoProps {
  label: string;
  /** Breakdown lines under the help text, e.g. one per failure reason. */
  details?: string[];
}

export function MetricInfo({ label, details = [] }: MetricInfoProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [side, setSide] = useState<'right' | 'bottom'>('right');
  // The page content, not the card: the tooltip may overlap the next card but never the sidebar.
  const [page, setPage] = useState<HTMLElement | null>(null);
  const onOpenChange = (open: boolean) => {
    const content = triggerRef.current?.closest('main') ?? null;
    const trigger = triggerRef.current?.getBoundingClientRect();
    const bounds = content?.getBoundingClientRect();
    setPage(content);
    if (open && trigger && bounds) setSide(bounds.right - EDGE_PADDING - trigger.right >= MIN_RIGHT_ROOM ? 'right' : 'bottom');
  };
  return (
    <Tooltip onOpenChange={onOpenChange}>
      <TooltipTrigger asChild>
        <button
          ref={triggerRef}
          type='button'
          aria-label={[label, ...details].join('\n')}
          className='inline-flex size-4 shrink-0 items-center justify-center rounded-full text-gray-800 hover:text-black-300 focus-visible:outline-2 focus-visible:outline-offset-2'
        >
          <InfoIcon aria-hidden='true' className='size-3' strokeWidth={1.5} />
        </button>
      </TooltipTrigger>
      {/* Right never flips left; below shifts sideways to stay on the page and can't flip up, as the icon sits in the card header. */}
      <TooltipContent
        variant='dark'
        side={side}
        align={side === 'right' ? 'start' : 'center'}
        alignOffset={side === 'right' ? -6 : 0}
        sideOffset={5}
        avoidCollisions={side === 'bottom'}
        collisionBoundary={page ?? []}
        collisionPadding={EDGE_PADDING}
        className='max-w-[min(320px,var(--radix-tooltip-content-available-width))] rounded-md px-3 py-2 text-left text-xs leading-relaxed text-pretty'
      >
        <p>{label}</p>
        {details.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
